import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  useToast,
  useColorModeValue,
  Icon,
  Collapse,
  Image,
  Badge,
  InputGroup,
  InputRightElement,
  Flex,
  Grid,
  GridItem,
  SimpleGrid
} from '@chakra-ui/react';
import { FaPaw, FaTint, FaRupeeSign, FaCamera, FaVideo, FaMapMarkerAlt, FaChevronDown, FaChevronUp, FaCheck, FaTrash } from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../translations/useTranslation';
import { API_BASE_URL, GEOAPIFY_API_KEY } from '../config/api_keys';
import SelectLocation from '../SellFormComponents/SelectLocation';
import Loading from '../resources/Loading';

// Log API URL for debugging
console.log('API_BASE_URL:', API_BASE_URL);

const SellAnimal = ({ animalType: initialAnimalType = 'Cow' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [loading, setLoading] = useState(false);

  // Form states
  const [animalType, setAnimalType] = useState(initialAnimalType);
  const [lactationCycle, setLactationCycle] = useState('');
  const [currentMilkPerDay, setCurrentMilkPerDay] = useState('');
  const [price, setPrice] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  
  // Media states
  const [videoUrl, setVideoUrl] = useState('');
  const [udderPhotoUrl, setUdderPhotoUrl] = useState('');
  const [milkingVideoUrl, setMilkingVideoUrl] = useState('');
  const [animalImages, setAnimalImages] = useState([]); // New state for multiple images
  
  // Location states
  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState(null);
  const [coordinates, setCoordinates] = useState(null);

  const videoInputRef = useRef(null);
  const udderPhotoInputRef = useRef(null);
  const milkingVideoInputRef = useRef(null);
  const animalImagesInputRef = useRef(null); // New ref for multiple images

  const handleLocationChange = (data) => {
    console.log('Location data received:', data);
    setLocation(data.location);
    setAddress(data.address);
    
    // Handle coordinates from SelectLocation component
    if (data.coordinates) {
      setCoordinates(data.coordinates);
      // Also update location with coordinates for validation
      setLocation(prev => ({
        ...prev,
        coordinates: data.coordinates
      }));
    }
  };

  // New function to handle multiple image uploads
  const handleMultipleImageUpload = async (files) => {
    try {
      console.log('Uploading multiple images:', files.length, 'files');
      
      // Create FormData with all files
      const formData = new FormData();
      Array.from(files).forEach((file, index) => {
        formData.append('images', file);
      });
      
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        toast({
          title: 'Error',
          description: 'Please login to upload files',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }
      
      const response = await axios.post(`${API_BASE_URL}/api/animals/upload-images`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });
      
      console.log('Upload response:', response.data);
      
      // Add new images to existing images
      setAnimalImages(prev => [...prev, ...response.data.imageUrls]);
      
      toast({
        title: 'Success',
        description: `${files.length} image(s) uploaded successfully`,
        status: 'success',
        duration: 2000,
        isClosable: true
      });
    } catch (error) {
      console.error('Error uploading images:', error);
      console.error('Error response:', error.response?.data);
      toast({
        title: 'Error',
        description: error.response?.data?.error || error.response?.data?.details || 'Failed to upload images',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  const handleVideoUpload = async (file) => {
    try {
      console.log('Uploading video:', file.name, file.size, file.type);
      const formData = new FormData();
      formData.append('video', file);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        toast({
          title: 'Error',
          description: 'Please login to upload files',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }
      
      const response = await axios.post(`${API_BASE_URL}/api/animals/upload-video`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - let axios set it automatically with boundary
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });
      
      console.log('Upload response:', response.data);
      setVideoUrl(response.data.videoUrl);
      toast({
        title: 'Success',
        description: 'Video uploaded successfully',
        status: 'success',
        duration: 2000,
        isClosable: true
      });
    } catch (error) {
      console.error('Error uploading video:', error);
      console.error('Error response:', error.response?.data);
      toast({
        title: 'Error',
        description: error.response?.data?.error || error.response?.data?.details || 'Failed to upload video',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  const handlePhotoUpload = async (file) => {
    try {
      console.log('Uploading photo:', file.name, file.size, file.type);
      const formData = new FormData();
      formData.append('photo', file);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        toast({
          title: 'Error',
          description: 'Please login to upload files',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }
      
      const response = await axios.post(`${API_BASE_URL}/api/animals/upload-photo`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - let axios set it automatically with boundary
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });
      
      console.log('Upload response:', response.data);
      setUdderPhotoUrl(response.data.photoUrl);
      toast({
        title: 'Success',
        description: 'Photo uploaded successfully',
        status: 'success',
        duration: 2000,
        isClosable: true
      });
    } catch (error) {
      console.error('Error uploading photo:', error);
      console.error('Error response:', error.response?.data);
      toast({
        title: 'Error',
        description: error.response?.data?.error || error.response?.data?.details || 'Failed to upload photo',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  const handleMilkingVideoUpload = async (file) => {
    try {
      console.log('Uploading milking video:', file.name, file.size, file.type);
      const formData = new FormData();
      formData.append('video', file);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        toast({
          title: 'Error',
          description: 'Please login to upload files',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }
      
      const response = await axios.post(`${API_BASE_URL}/api/animals/upload-video`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - let axios set it automatically with boundary
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });
      
      console.log('Upload response:', response.data);
      setMilkingVideoUrl(response.data.videoUrl);
      toast({
        title: 'Success',
        description: 'Milking video uploaded successfully',
        status: 'success',
        duration: 2000,
        isClosable: true
      });
    } catch (error) {
      console.error('Error uploading milking video:', error);
      console.error('Error response:', error.response?.data);
      toast({
        title: 'Error',
        description: error.response?.data?.error || error.response?.data?.details || 'Failed to upload milking video',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  // Function to remove an image
  const removeImage = (index) => {
    setAnimalImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleCheckRate = () => {
    // Placeholder for rate checking functionality
    toast({
      title: 'Rate Check',
      description: 'Rate checking feature coming soon',
      status: 'info',
      duration: 3000,
      isClosable: true
    });
  };

  const handleSubmit = async () => {
    // Validation
    if (!animalType) {
      toast({
        title: 'Error',
        description: 'Please select animal type',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (!lactationCycle) {
      toast({
        title: 'Error',
        description: 'Please select lactation cycle',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (!currentMilkPerDay || !price) {
      toast({
        title: 'Error',
        description: 'Please fill in milk production and price',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (!address || !address.state || !address.city) {
      toast({
        title: 'Error',
        description: 'Please select location',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      
      // Check for coordinates in multiple possible formats
      let finalCoordinates = coordinates || 
                            (location && location.coordinates) ||
                            (address && address.coordinates);
      
      // If we have address but no coordinates, try to fetch them
      if (!finalCoordinates && address && address.state && address.city) {
        console.log('Address selected but coordinates missing, fetching coordinates...');
        try {
          // Fetch coordinates for the selected city
          const geoResponse = await axios.get(
            `https://api.geoapify.com/v1/geocode/search?city=${encodeURIComponent(address.city)}&state=${encodeURIComponent(address.state)}&country=India&apiKey=${GEOAPIFY_API_KEY}`
          );
          
          if (geoResponse.data.features && geoResponse.data.features.length > 0) {
            const coords = geoResponse.data.features[0].geometry.coordinates;
            finalCoordinates = { 
              longitude: coords[0],
              latitude: coords[1]
            };
            // Update state for future use
            setCoordinates(finalCoordinates);
            setLocation(prev => ({
              ...prev,
              coordinates: finalCoordinates
            }));
            console.log('Coordinates fetched successfully:', coords);
          } else {
            throw new Error('No coordinates found');
          }
        } catch (geoError) {
          console.error('Error fetching coordinates:', geoError);
          toast({
            title: 'Error',
            description: 'Could not get location coordinates. Please use GPS location or select location on map.',
            status: 'error',
            duration: 5000,
            isClosable: true
          });
          setLoading(false);
          return;
        }
      }
      
      if (!finalCoordinates) {
        console.log('Location state:', { location, address, coordinates });
        toast({
          title: 'Error',
          description: 'Please select location coordinates on the map or use GPS location',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        setLoading(false);
        return;
      }
      
      // Prepare location data - handle multiple coordinate formats
      let locationData;
      
      // Process coordinates based on format
      if (finalCoordinates) {
        if (finalCoordinates.longitude !== undefined && finalCoordinates.latitude !== undefined) {
          locationData = {
            coordinates: [
              parseFloat(finalCoordinates.longitude),
              parseFloat(finalCoordinates.latitude)
            ]
          };
        } else if (Array.isArray(finalCoordinates) && finalCoordinates.length >= 2) {
          locationData = {
            coordinates: [
              parseFloat(finalCoordinates[0]),
              parseFloat(finalCoordinates[1])
            ]
          };
        }
      }

      if (!locationData || !locationData.coordinates || locationData.coordinates.length !== 2) {
        console.error('Invalid coordinates:', { location, address, coordinates });
        toast({
          title: 'Error',
          description: 'Invalid location coordinates. Please select location again using the map or GPS.',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        setLoading(false);
        return;
      }
      
      console.log('Location data prepared:', locationData);

      // Generate title and description from form data
      const lactationLabels = {
        'not_calved': t('notCalved'),
        'first': t('first'),
        'second': t('second'),
        'other': t('other')
      };
      const title = `${animalType} - ${lactationLabels[lactationCycle] || ''}`;
      const description = `${animalType} with ${currentMilkPerDay} liters milk per day. ${additionalInfo || ''}`;

      const animalData = {
        animalType,
        title,
        description,
        price: Number(price),
        sellingTimeline: '1-3 days', // Default, can be made configurable
        animalSource: 'tethered', // Default, can be made configurable
        lactationCycle,
        currentMilkPerDay: Number(currentMilkPerDay),
        videoUrl,
        udderPhotoUrl,
        milkingVideoUrl,
        images: [...animalImages, ...(udderPhotoUrl ? [udderPhotoUrl] : [])], // Combine all images
        location: locationData,
        address: {
          state: address.state,
          city: address.city,
          area: address.area || '',
          postcode: address.postcode || '',
          fullAddress: address.fullAddress || `${address.area || ''}, ${address.city}, ${address.state}`.trim()
        },
        animalDetails: {},
        additionalInfo
      };

      const response = await axios.post(`${API_BASE_URL}/api/animals/sell`, animalData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      toast({
        title: 'Success',
        description: 'Animal listed successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true
      });

      navigate('/livestock');
    } catch (error) {
      console.error('Error submitting animal:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to list animal',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxW="container.md" py={4}>
      <VStack spacing={6} align="stretch">
        {/* Animal Type Section */}
        <Box>
          <HStack mb={3}>
            <Icon as={FaPaw} color="green.500" />
            <Text fontWeight="bold" fontSize="lg">{t('whichAnimal')} *</Text>
          </HStack>
          <HStack spacing={3}>
            {['Cow', 'Buffalo', 'Other'].map((type) => (
              <Button
                key={type}
                size="md"
                borderRadius="full"
                colorScheme={animalType === type ? 'green' : 'gray'}
                variant={animalType === type ? 'solid' : 'outline'}
                onClick={() => setAnimalType(type)}
                leftIcon={type === 'Other' ? <Icon as={FaChevronDown} /> : undefined}
              >
                {type === 'Cow' ? t('cows') : type === 'Buffalo' ? t('buffaloes') : t('other')}
              </Button>
            ))}
          </HStack>
        </Box>

        {/* Lactation Cycle Section */}
        <Box>
          <HStack mb={3}>
            <Icon as={FaPaw} color="green.500" />
            <Text fontWeight="bold" fontSize="lg">{t('whichLactation')} *</Text>
          </HStack>
          <HStack spacing={3} flexWrap="wrap">
            {[
              { value: 'not_calved', label: t('notCalved') },
              { value: 'first', label: t('first') },
              { value: 'second', label: t('second') },
              { value: 'other', label: t('other') }
            ].map((cycle) => (
              <Button
                key={cycle.value}
                size="md"
                borderRadius="full"
                colorScheme={lactationCycle === cycle.value ? 'green' : 'gray'}
                variant={lactationCycle === cycle.value ? 'solid' : 'outline'}
                onClick={() => setLactationCycle(cycle.value)}
                leftIcon={cycle.value === 'other' ? <Icon as={FaChevronDown} /> : undefined}
              >
                {cycle.label}
              </Button>
            ))}
          </HStack>
        </Box>

        {/* Current Milk Per Day Section */}
        <Box>
          <HStack mb={2}>
            <Icon as={FaTint} color="green.500" />
            <Text fontWeight="bold" fontSize="lg">{t('currentMilkPerDay')} *</Text>
          </HStack>
          <Text fontSize="sm" color="gray.500" mb={2}>{t('totalMilkToday')}</Text>
          <InputGroup>
            <Input
              type="number"
              value={currentMilkPerDay}
              onChange={(e) => setCurrentMilkPerDay(e.target.value)}
              placeholder={t('milkPlaceholder')}
            />
            <InputRightElement width="4.5rem">
              <Text fontSize="sm" color="gray.500">{t('liter')}</Text>
            </InputRightElement>
          </InputGroup>
        </Box>

        {/* Rate/Price Section */}
        <Box>
          <HStack mb={2}>
            <Icon as={FaRupeeSign} color="green.500" />
            <Text fontWeight="bold" fontSize="lg">{t('rate')} (₹) *</Text>
          </HStack>
          <Text fontSize="sm" color="gray.500" mb={2}>{t('enterCorrectRate')}</Text>
          <InputGroup>
            <Input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder={t('pricePlaceholder')}
            />
            <InputRightElement width="4.5rem">
              <Text fontSize="sm" color="gray.500">{t('rupee')}</Text>
            </InputRightElement>
          </InputGroup>
        </Box>

        {/* Check Rate Button */}
        <Button
          colorScheme="blue"
          variant="outline"
          leftIcon={<Icon as={FaRupeeSign} />}
          rightIcon={<Text>→</Text>}
          onClick={handleCheckRate}
        >
          {t('checkCorrectRate')}
        </Button>

        {/* Media Upload Section */}
        <Box>
          <HStack mb={2}>
            <Icon as={FaCamera} color="green.500" />
            <Text fontWeight="bold" fontSize="lg">{t('uploadVideoOrPhoto')}</Text>
          </HStack>
          <Text fontSize="sm" color="gray.500" mb={4}>{t('goodPhotosSellFast')}</Text>
          
          <VStack spacing={4} align="stretch">
            {/* Multiple Images Upload */}
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">Animal Images (Upload multiple photos)</Text>
              <Box
                borderWidth="2px"
                borderStyle="dashed"
                borderColor={borderColor}
                borderRadius="lg"
                p={4}
                textAlign="center"
              >
                <input
                  ref={animalImagesInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files && files.length > 0) handleMultipleImageUpload(files);
                  }}
                />
                <Button
                  colorScheme="blue"
                  size="sm"
                  onClick={() => animalImagesInputRef.current?.click()}
                  leftIcon={<Icon as={FaCamera} />}
                >
                  Choose Images
                </Button>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  You can select multiple images at once
                </Text>
              </Box>
              
              {/* Preview of uploaded images */}
              {animalImages.length > 0 && (
                <SimpleGrid columns={3} spacing={2} mt={3}>
                  {animalImages.map((img, index) => (
                    <Box key={index} position="relative">
                      <Image 
                        src={img} 
                        alt={`Animal ${index + 1}`} 
                        borderRadius="md" 
                        objectFit="cover"
                        h="100px"
                        w="100%"
                      />
                      <Button
                        size="xs"
                        colorScheme="red"
                        position="absolute"
                        top={1}
                        right={1}
                        onClick={() => removeImage(index)}
                      >
                        <Icon as={FaTrash} />
                      </Button>
                    </Box>
                  ))}
                </SimpleGrid>
              )}
            </Box>

            {/* Video and Photo in two columns */}
            <HStack spacing={4} align="stretch">
              {/* Video Upload */}
              <Box
                flex={1}
                borderWidth="2px"
                borderStyle="dashed"
                borderColor={borderColor}
                borderRadius="lg"
                p={4}
                textAlign="center"
              >
                <Text mb={2} fontSize="sm" fontWeight="medium">{t('chooseVideo')}</Text>
                {videoUrl ? (
                  <VStack>
                    <Badge colorScheme="green">Uploaded</Badge>
                    <Button size="xs" onClick={() => videoInputRef.current?.click()}>
                      {t('change')}
                    </Button>
                  </VStack>
                ) : (
                  <>
                    <Icon as={FaVideo} boxSize={6} color="gray.400" mb={2} />
                    <Button
                      colorScheme="blue"
                      size="sm"
                      onClick={() => videoInputRef.current?.click()}
                    >
                      {t('chooseVideo')}
                    </Button>
                  </>
                )}
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleVideoUpload(file);
                  }}
                />
              </Box>
            </HStack>

            {/* Milking Video Upload */}
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">{t('uploadMilkingVideo')}</Text>
              {milkingVideoUrl ? (
                <HStack>
                  <Badge colorScheme="green">Video Uploaded</Badge>
                  <Image src={milkingVideoUrl} alt="Milking" maxH="80px" borderRadius="md" />
                  <Button size="sm" onClick={() => milkingVideoInputRef.current?.click()}>
                    {t('change')}
                  </Button>
                </HStack>
              ) : (
                <Box
                  borderWidth="2px"
                  borderStyle="dashed"
                  borderColor={borderColor}
                  borderRadius="lg"
                  p={4}
                  textAlign="center"
                >
                  <Image
                    src="https://via.placeholder.com/150x100?text=Milking+Video"
                    alt="Milking placeholder"
                    maxH="80px"
                    borderRadius="md"
                    mb={2}
                    opacity={0.5}
                  />
                  <Button
                    colorScheme="blue"
                    size="sm"
                    onClick={() => milkingVideoInputRef.current?.click()}
                  >
                    {t('chooseVideo')}
                  </Button>
                </Box>
              )}
              <input
                ref={milkingVideoInputRef}
                type="file"
                accept="video/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleMilkingVideoUpload(file);
                }}
              />
            </Box>
          </VStack>
        </Box>

        {/* Additional Information Section */}
        <Box>
          <Button
            width="100%"
            justifyContent="space-between"
            onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}
            colorScheme="purple"
            borderRadius="lg"
            rightIcon={<Icon as={showAdditionalInfo ? FaChevronUp : FaChevronDown} />}
          >
            <Text>{t('additionalInformation')}</Text>
          </Button>
          <Collapse in={showAdditionalInfo} animateOpacity>
            <Box mt={4} p={4} bg={bgColor} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
              <Textarea
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                placeholder={t('addMoreInfoPlaceholder')}
                rows={4}
              />
            </Box>
          </Collapse>
        </Box>

        {/* Location Section */}
        <Box>
          <HStack mb={2}>
            <Icon as={FaMapMarkerAlt} color="green.500" />
            <Text fontWeight="bold" fontSize="lg">{t('location')}</Text>
          </HStack>
          <Text fontSize="sm" color="gray.500" mb={3}>{t('buyersWillSeeAnimal')}</Text>
          <SelectLocation onAddressSelect={handleLocationChange} />
          {address && (
            <HStack mt={3} p={3} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor={borderColor}>
              <Text fontSize="sm" flex={1}>
                {address.fullAddress || `${address.area || ''}, ${address.city}, ${address.state}`.trim()}
              </Text>
              <Button size="sm" colorScheme="green" variant="outline">
                {t('change')}
              </Button>
            </HStack>
          )}
        </Box>

        {/* Live Buyers Info */}
        <Box bg="green.50" p={3} borderRadius="md" borderWidth="1px" borderColor="green.200">
          <HStack>
            <Badge colorScheme="red" fontSize="sm">LIVE</Badge>
            <Text fontSize="sm" color="gray.700">
              200 {t('buyersLookingForAnimals')}
            </Text>
          </HStack>
        </Box>

        {/* Submit Button */}
        <Button
          colorScheme="green"
          size="lg"
          width="100%"
          onClick={handleSubmit}
          isLoading={loading}
          loadingText={t('register')}
        >
          {t('register')}
        </Button>
      </VStack>
    </Container>
  );
};

export default SellAnimal;